import {
  Alert,
  Button,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import type { ApiError } from "@entrilia/shared/api/types";
import DialogButtons from "@entrilia/shared/components/DialogButtons";
import SmallDialog from "@entrilia/shared/components/SmallDialog";
import { logError } from "@entrilia/shared/logging";
import type { EmailAccountDeletionOutcome, EmailAccountInfo, SmtpLoginStatus } from "../../../api/emailAccountsTypes";
import { api } from "../../../api/operationsApi";

interface Props {
  clientCode: string;
  account: EmailAccountInfo;
  clientAccounts: EmailAccountInfo[];
  defaultAccountId?: string;
  smtpLoginStatus?: SmtpLoginStatus;
  onClose: (wasAttempted: boolean) => void;
}

const deleteEmailAccount = withErrorHandling(api.emailAccounts.deleteEmailAccount);

const getResultMessage = (
  outcome: EmailAccountDeletionOutcome,
  account: EmailAccountInfo,
  wasRemovalAnnounced: boolean,
  hasNoSmtpLogin: boolean
): string | undefined => {
  switch (outcome) {
    case "RemovedFromClient":
      return undefined;
    case "Deleted":
      if (!wasRemovalAnnounced) {
        return undefined;
      }

      return hasNoSmtpLogin
        ? `${account.address} was deleted: the other clients it listed no longer belong to this organization.`
        : `${account.address} was deleted, with its SMTP login: the other clients it listed no longer belong to this organization.`;
    case "DeletedSmtpLoginKept":
      return hasNoSmtpLogin
        ? `${account.address} was deleted.`
        : `${account.address} was deleted. Its SMTP login stays in Mailgun because another organization uses the same address.`;
    case "DeletedSmtpLoginKeptDomainNotHeld":
      return hasNoSmtpLogin
        ? `${account.address} was deleted.`
        : `${account.address} was deleted. Its SMTP login stays in Mailgun because this organization no longer holds ${account.domainName}; remove it there by hand if nobody uses it.`;
  }
};

const DeleteEmailAccountDialog = ({
  clientCode,
  account,
  clientAccounts,
  defaultAccountId,
  smtpLoginStatus,
  onClose,
}: Props) => {
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<ApiError>();
  const [wasAttempted, setWasAttempted] = useState(false);
  const [resultMessage, setResultMessage] = useState<string>();
  const [newDefaultAccountId, setNewDefaultAccountId] = useState("");

  const otherClientCodes = account.clients.map((client) => client.clientCode).filter((code) => code !== clientCode);
  const isUsedByOtherClients = otherClientCodes.length > 0;
  const hasNoSmtpLogin = smtpLoginStatus === "Absent";

  const remainingActiveAccounts = clientAccounts.filter((other) => other.isUsable && other.id !== account.id);
  const isDefaultKept = remainingActiveAccounts.some((other) => other.id === defaultAccountId);
  const onlyRemainingAccount = remainingActiveAccounts.length === 1 ? remainingActiveAccounts[0] : undefined;
  const mustChooseNewDefault = !isDefaultKept && remainingActiveAccounts.length > 1;

  const handleDeleteClick = async () => {
    setIsDeleting(true);
    setDeleteError(undefined);
    setWasAttempted(true);

    const request = { newDefaultAccountId: mustChooseNewDefault ? newDefaultAccountId : undefined };
    const [response, error] = await deleteEmailAccount(clientCode, account.id, request);
    setIsDeleting(false);
    if (error) {
      logError(error, "[DeleteEmailAccountDialog] deleteEmailAccount");
      setDeleteError(error);
      return;
    }

    const message = getResultMessage(response.outcome, account, isUsedByOtherClients, hasNoSmtpLogin);
    if (message === undefined) {
      onClose(true);
      return;
    }

    setResultMessage(message);
  };

  const handleClose = () => {
    if (isDeleting) {
      return;
    }

    onClose(wasAttempted);
  };

  return (
    <SmallDialog open onClose={handleClose}>
      <DialogTitle>{isUsedByOtherClients ? "Remove email account" : "Delete email account"}</DialogTitle>
      <DialogContent>
        {resultMessage ? (
          <Alert severity="success">{resultMessage}</Alert>
        ) : (
          <Stack spacing={2}>
            {isUsedByOtherClients ? (
              <Typography>
                Remove {account.address} from {clientCode}? It stays with {otherClientCodes.join(", ")}
                {hasNoSmtpLogin ? "." : ", and its SMTP login keeps working."}
              </Typography>
            ) : (
              <>
                <Typography>Delete {account.address}?</Typography>
                {!hasNoSmtpLogin && (
                  <Alert severity="warning">
                    Its SMTP login is deleted in Mailgun and stops working in Business Central. The login stays only if
                    another organization uses the same address or this organization no longer holds its domain.
                  </Alert>
                )}
              </>
            )}
            {!isDefaultKept && remainingActiveAccounts.length === 0 && (
              <Alert severity="warning">There are no active email accounts for {clientCode} after this.</Alert>
            )}
            {!isDefaultKept && onlyRemainingAccount && (
              <Alert severity="info">{onlyRemainingAccount.address} becomes the default sender.</Alert>
            )}
            {mustChooseNewDefault && (
              <FormControl fullWidth>
                <InputLabel id="delete-email-account-new-default">New default sender</InputLabel>
                <Select
                  labelId="delete-email-account-new-default"
                  label="New default sender"
                  value={newDefaultAccountId}
                  disabled={isDeleting}
                  onChange={(event) => setNewDefaultAccountId(event.target.value)}
                >
                  {remainingActiveAccounts.map((other) => (
                    <MenuItem key={other.id} value={other.id}>
                      {other.address}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            <Alert severity="info">
              Remove the account from this client in Business Central too, or the next import brings it back.
            </Alert>
            {deleteError && <Alert severity="error">{deleteError.message}</Alert>}
          </Stack>
        )}
      </DialogContent>
      <DialogButtons onCancel={handleClose} cancelText={resultMessage ? "Close" : undefined}>
        {!resultMessage && (
          <Button
            variant="contained"
            color="error"
            loading={isDeleting}
            disabled={mustChooseNewDefault && newDefaultAccountId === ""}
            onClick={handleDeleteClick}
          >
            {isUsedByOtherClients ? "Remove" : "Delete"}
          </Button>
        )}
      </DialogButtons>
    </SmallDialog>
  );
};

export default DeleteEmailAccountDialog;
